<template>
  <div
    class="tab-button"
    :class="{
      'tab-button--selected': selected,
      '-not-removable': closable == false
    }"
    @contextmenu="openContextMenu($event)"
  >
    <div class="tab-button__title">
      <button
        type="button"
        class="tab-button__text"
        @click="$emit('select')"
        @dblclick="startRenaming()"
      >
        <span>
          <slot name="icon"></slot>
        </span>
        <slot name="default">
          {{ title }}
        </slot>
      </button>
    </div>
    <form
      v-show="renaming"
      class="tab-button__renameoverlay"
      @submit.prevent="saveNewTitle"
    >
      <input
        ref="renameInput"
        v-model="newTitle"
        type="text"
        class="tab-button__renameinput"
        :placeholder="defaultTitle"
        @blur="saveNewTitle"
      />
      <button class="button-icon" type="submit">
        <lucide-icon name="check" />
      </button>
    </form>

    <button
      ref="contextmenuButton"
      type="button"
      class="button-icon ml-auto"
      @click="openContextMenu($event)"
    >
      <lucide-icon name="more-horizontal" />
    </button>

    <button
      v-if="closable"
      class="button-icon"
      type="button"
      @click="emit('close')"
    >
      <lucide-icon name="x" />
    </button>

    <context-menu
      v-if="contextMenuSettings.visible"
      :x="contextMenuSettings.x"
      :y="contextMenuSettings.y"
      @close="contextMenuSettings.visible = false"
    >
      <slot name="context-menu" :start-renaming="startRenaming"></slot>
    </context-menu>
  </div>
</template>

<script setup lang="ts">
import { ref, nextTick } from "vue";

import ContextMenu from "@core/ui/ContextMenu.vue";
import LucideIcon from "./LucideIcon.vue";

const props = withDefaults(
  defineProps<{
    title?: string;
    defaultTitle?: string;
    selected?: boolean;
    renamable?: boolean;
    closable?: boolean;
  }>(),
  {
    title: "",
    defaultTitle: "",
    selected: false,
    renamable: true,
    closable: true
  }
);

const emit = defineEmits<{
  (e: "close"): void;
  (e: "update:title", title: string): void;
  (e: "select"): void;
}>();

const contextmenuButton = ref<HTMLButtonElement>();
const renameInput = ref<HTMLInputElement>();

const renaming = ref(false);
const newTitle = ref("");
const contextMenuSettings = ref({
  visible: false,
  x: 0,
  y: 0
});

function openContextMenu(event: MouseEvent) {
  event.preventDefault();
  const button = contextmenuButton.value;
  if (button) {
    const buttonRect = button.getBoundingClientRect();
    contextMenuSettings.value.x = buttonRect.left + 5;
    contextMenuSettings.value.y = buttonRect.bottom - 3;
  } else {
    contextMenuSettings.value.x = event.clientX;
    contextMenuSettings.value.y = event.clientY;
  }

  contextMenuSettings.value.visible = true;
}

function startRenaming() {
  if (!props.renamable) return;

  newTitle.value = props.title || props.defaultTitle;
  renaming.value = true;

  nextTick(() => {
    renameInput.value?.select();
    renameInput.value?.focus();
  });
}

function saveNewTitle() {
  if (renaming.value == false) return;
  if (renameInput.value == null) return;

  emit("update:title", newTitle.value);

  renaming.value = false;
}
</script>

<style lang="scss">
.tab-button {
  --bg: var(--color-background);
  position: relative;
  display: grid;
  grid-template-columns: minmax(0, 1fr) 2rem 3rem;
  white-space: nowrap;
  width: 20rem;
  max-width: 100%;
  background-color: var(--bg);
  border-top-left-radius: 10px;
  border-top-right-radius: 10px;
  box-shadow: 0 0 3px var(--color-shadow-1);
  // inset 0 -2px 6px -2px var(--color-shadow-1),
  border: none;
  border-bottom: none;

  &.-not-removable {
    width: max-content;
    border-top-left-radius: 0px;
    border-top-right-radius: 0px;
    box-shadow: 0 2px 3px var(--color-shadow-1);
    grid-template-columns: minmax(0, 1fr) 3rem;
    min-width: max-content;
  }

  &__title {
    width: 100%;
    overflow: hidden;
  }

  &__text {
    width: 100%;
    padding: 0.75rem 1rem;
    color: var(--color-text-medium);
    text-align: left;
    border: none;
    background-color: transparent;
    font-size: inherit;
    font-weight: 700;
    cursor: pointer;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
    display: block;
    transition: color 125ms;
  }
  &__text:hover {
    color: var(--color-text);
    --bg: var(--color-background);
  }

  &__renameoverlay {
    position: absolute;
    top: 0;
    left: 0;
    bottom: 0;
    right: 0;
    display: grid;
    grid-template-columns: 1fr 3rem;
    background-color: var(--color-background);
    overflow: hidden;
    border-radius: inherit;
  }
  &__renameinput {
    min-width: 0;
    appearance: none;
    border: none;
    padding: 0.25rem 1rem;
    border-top-left-radius: inherit;

    &:focus {
      outline: none;
      box-shadow: inset 0 0 0 1px var(--color-action);
    }
  }

  &--selected {
    box-shadow: 0 0 3px var(--color-shadow-2);
    border-color: var(--color-action);
    --bg: var(--color-background-close);
  }
  &--selected > &__text {
    color: var(--color-action);
  }
}
</style>
